import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import Site from "../../model/Site";
import { useApiClient } from "../../contexts/ApiClientContext";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import Loading from "../common/Loading";
import Button from "../common/button/Button";
import UserRoles from "../../model/enums/UserRole";
import ButtonAction from "../common/button/ButtonAction";
import SitesListElement from "./SitesListElement";
import AppIcons from "../../utils/AppIcons";
import { Tooltip } from "react-tooltip";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";

/*
  Component to manage the display of the existing SITES list
*/
function SitesList() {
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the navigator
  const navigate = useNavigate();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // activities information to be displayed
  const [data, setData] = useState<Site[]>([]);
  // manage data loading animation
  const [loading, setLoading] = useState(true);

  // load record data from the server
  useEffect(() => {
    apiClient
      ?.get<PagedData<Site[]>>({
        url: APIEndPoints.SITES.ALL,
        requiresToken: true,
      })
      .then((data: PagedData<Site[]>) => {
        setData(data.items);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [apiClient, navigate]);

  if (loading) {
    return <Loading />;
  }

  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (authManager.isAdmin() ||
      authManager.isModerator() ||
      authManager.isSpecialist() );

  return (
    <div>
      <div className="list-page-header">
        <div className="list-page-title">
          <h1>
            <span>{AppIcons.SITE}</span>
            Sites
            <span className="tooltip-hint">
              <a data-tooltip-id="form-tooltip" data-tooltip-place="right">
                {AppIcons.INFO}
              </a>
              <Tooltip id="form-tooltip">
                Refers to all topographical areas and landscapes, the works of
                <br />
                man or the combined works of nature and of man, including
                <br />
                historic parks and gardens, which are of value from the
                <br />
                archaeological, historical, aesthetic, ethnological or
                <br />
                anthropological point of view.
              </Tooltip>
            </span>
          </h1>
        </div>
        <div>
          {canEdit && (
            <Button
              label={"Create a new site"}
              purpose={ButtonAction.CREATE}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.SITES,
                    subsection: AppSubSections.CREATE,
                  }),
                )
              }
            />
          )}
        </div>
      </div>
      {data?.length === 0 && (
        <div className="list-no-records">
          No records found. Change search parameters and try again.
        </div>
      )}
      {data?.length > 0 &&
        data.map((item) => {
          // check EDIT rights
          const canEdit =
            !EmbeddingToolInfo.isChemiTool() &&
            (item.id_user === authManager.getUserID() ||
              authManager.getUserRole() === UserRoles.ADMIN);

          return (
            <SitesListElement canEdit={canEdit} data={item} key={item.id} />
          );
        })}
    </div>
  );
}

export default SitesList;
